import { useEffect, useRef } from 'react'
import Lightbox from 'yet-another-react-lightbox'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import 'yet-another-react-lightbox/styles.css'
import { resolveOriginalKey } from '@/hooks/useOfflineRichContentMediaUrls'

interface ImageLightboxProps {
  src: string | null
  alt?: string
  /** Needed for blob: URLs not created by the rich content cache, whose extension cannot be resolved. */
  mimeType?: string
  onClose: () => void
}

export function ImageLightbox({ src, alt, mimeType, onClose }: ImageLightboxProps) {
  const historyPushedRef = useRef(false)
  const onCloseRef = useRef(onClose)

  // Keep onClose ref up to date
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  // Push a history state when the modal opens so the back button can close it
  useEffect(() => {
    if (!src) {
      historyPushedRef.current = false
      return
    }

    // Only push history state once when modal opens
    if (!historyPushedRef.current) {
      window.history.pushState({ imageLightbox: true }, '')
      historyPushedRef.current = true
    }

    // Listen for back button
    const handlePopState = (e: PopStateEvent) => {
      // Only close if we're navigating away from the image lightbox state
      if (!e.state?.imageLightbox) {
        onCloseRef.current()
      }
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
      historyPushedRef.current = false
    }
  }, [src])

  const handleClose = () => {
    // Always go back in history when closing
    // The popstate handler will then call onClose()
    if (window.history.state?.imageLightbox) {
      window.history.back()
    } else {
      // Fallback: if there's no history state (edge case), close directly
      onCloseRef.current()
    }
  }

  // SVGs have no intrinsic pixel dimensions; the lightbox reads naturalWidth/naturalHeight
  // from the rendered <img>, gets 0×0, and renders the slide tiny. Providing large synthetic
  // dimensions lets the lightbox allocate full space; object-fit: contain handles the rest.
  // For blob: URLs the extension is gone, so resolve back to the original cache key first.
  const effectiveSrc = src?.startsWith('blob:') ? (resolveOriginalKey(src) ?? src) : src
  const isSvg = mimeType === 'image/svg+xml' || (!!effectiveSrc && /\.svg(\?|$)/i.test(effectiveSrc))

  return (
    <Lightbox
      open={!!src}
      close={handleClose}
      slides={src ? [{ src, alt: alt ?? '', ...(isSvg ? { width: 4096, height: 4096 } : {}) }] : []}
      plugins={[Zoom]}
      zoom={{
        maxZoomPixelRatio: 5,
        scrollToZoom: true,
      }}
      styles={{
        container: { backgroundColor: 'var(--color-scrim)' },
      }}
      carousel={{ finite: true }}
      controller={{ closeOnBackdropClick: true }}
    />
  )
}
